import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "../components/ui/navigation-menu";
import { Button } from "../components/ui/button";
import { COMPANY_NAME, TAGLINE } from '@/constants/util';
import { gradients } from '../constants/colors.jsx';
import { trackGA4Event } from '../utils/trackEvent.jsx';
import { PROFESSIONAL_DEVELOPMENT_PATH } from '../constants/professionalDevelopment.js';

// Islamic pattern SVG for the background
const islamicPatternSvg = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L45 30L30 60L15 30z' fill='white' fill-opacity='0.05'/%3E%3C/svg%3E")`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    resources: false
  });
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);
  const shouldScrollToFormRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Calculate header height on mount and when window resizes
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    
    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  // Handle scrolling to form when navigating to home page
  useEffect(() => {
    if (location.pathname === '/home' && shouldScrollToFormRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          const formElement = document.getElementById('loan-form');
          if (formElement) {
            const formPosition = formElement.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
              top: formPosition - 20,
              behavior: 'smooth'
            });
            shouldScrollToFormRef.current = false;
          }
        }, 100);
      });
    }
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleGetStarted = () => {
    // Track CTA click in GA4
    trackGA4Event('find_halal_funding_clicked', {
      button_location: 'header',
      button_text: 'Find Halal Funding',
      current_page: location.pathname
    });

    setIsMenuOpen(false);

    // Check if we're already on the home page
    if (location.pathname === '/home') {
      // Already on home page, just scroll to form
      const formElement = document.getElementById('loan-form');
      if (formElement) {
        const formPosition = formElement.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: formPosition - 20,
          behavior: 'smooth'
        });
      }
    } else {
      // Set flag to scroll after navigation
      shouldScrollToFormRef.current = true;
      // Navigate to home page (always use /home to ensure we get Home.jsx, not Landing)
      navigate('/home');
    }
  };

  const handleForCDFIs = () => {
    navigate('/partnerwithus');
    setIsMenuOpen(false);
  };

  return (
    <>
      <header ref={headerRef} className={`relative ${gradients.header} text-black py-3 md:py-4 px-4 md:px-6 shadow-sm border-b-2 border-white/10 z-50`}>
        <div 
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: islamicPatternSvg }}
        />

        <div className="relative z-10 max-w-[1400px] mx-auto flex items-center justify-between">
          <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-2 text-white no-underline">
            <img src="/CRED-free.png" alt="HalalCred Logo" className="h-[45px] md:h-[50px] w-auto" />
            <div>
              <h1 className="m-0 text-[1.75rem] font-bold bg-gradient-to-r from-green-900 to-[#347832] bg-clip-text text-transparent font-['Noto_Sans_Arabic']">
                {COMPANY_NAME}
              </h1>
              <p className="m-0 text-green-950 text-[0.875rem] md:text-[0.9375rem] opacity-95 pt-0.5 font-medium tracking-[0.01em]">
                {TAGLINE}
              </p>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <NavigationMenu>
              <NavigationMenuList className="gap-8">
                <NavigationMenuItem>
                  <Link 
                    to="/business-and-nonprofits"
                    className="text-green hover:text-green/90 text-[0.9375rem] font-bold transition-colors px-0 py-2 inline-block"
                  >
                    For Businesses & Nonprofits
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link 
                    to="/about-us"
                    className="text-green hover:text-green/90 text-[0.9375rem] font-bold transition-colors px-0 py-2 inline-block"
                  >
                    About Us
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-green hover:text-green/90 text-[0.9375rem] font-bold transition-colors bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[active]:bg-transparent relative group px-0 py-2">
                    Resources
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[500px] bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Link to="/faqs" className="block text-[0.9375rem] text-slate-700 hover:text-blue-600">
                            <h4 className="text-green-900 font-semibold mb-2">FAQs</h4>
                          </Link>
                          <Link to="/case-studies" className="block text-[0.9375rem] text-slate-700 hover:text-blue-600">
                            <h4 className="text-green-900 font-semibold mb-2">Case Studies</h4>
                          </Link>
                          <Link to={PROFESSIONAL_DEVELOPMENT_PATH} className="block text-[0.9375rem] text-slate-700 hover:text-blue-600">
                            <h4 className="text-green-900 font-semibold mb-2">Professional Development</h4>
                          </Link>
                          <Link to="/services" className="block text-[0.9375rem] text-slate-700 hover:text-blue-600">
                            <h4 className="text-green-900 font-semibold mb-2">Services</h4>
                          </Link>
                        </div>
                        <div className="space-y-2">
                          <Link to="/blog" className="block text-[0.9375rem] text-slate-700 hover:text-blue-600">
                            <h4 className="text-green-900 font-semibold mb-2">HalalCred Blog</h4>
                          </Link>
                          <Link to="/press-coverage" className="block text-[0.9375rem] text-slate-700 hover:text-blue-600">
                            <h4 className="text-green-900 font-semibold mb-2">Press Coverage</h4>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            
            <Button
              onClick={handleForCDFIs}
              className="bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 font-semibold px-6 py-2.5 rounded-full transition-all duration-200 transform hover:scale-105 active:scale-100 shadow-md hover:shadow-lg border border-green-500/20 mr-3"
            >
              Become a CDFI Partner
            </Button>
            <Button
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 font-semibold px-6 py-2.5 rounded-full transition-all duration-200 transform hover:scale-105 active:scale-100 shadow-md hover:shadow-lg border border-green-400/30"
            >
              Find Halal Funding
            </Button>
          </div>

          {/* Mobile Menu Button - Always visible */}
          <div className="md:hidden">
            <Button 
              variant="ghost" 
              className="p-2 hover:bg-green-100/10 relative z-50"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              <div className="flex flex-col gap-[6px] w-7">
                <span className={`block w-7 h-0.5 bg-green-900 transition-all duration-300 ease-in-out ${isMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
                <span className={`block w-7 h-0.5 bg-green-900 transition-all duration-300 ease-in-out ${isMenuOpen ? 'opacity-0' : ''}`} />
                <span className={`block w-7 h-0.5 bg-green-900 transition-all duration-300 ease-in-out ${isMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
              </div>
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar - Appears below the header */}
      {isMenuOpen && (
        <div className="md:hidden">
          {/* Backdrop overlay */}
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
            onClick={toggleMenu}
            style={{ top: headerHeight }}
          />
          
          {/* Sidebar */}
          <div 
            className="fixed right-0 w-[280px] bg-white shadow-xl overflow-y-auto z-50 transition-all duration-300 ease-in-out"
            style={{ 
              top: headerHeight,
              height: `calc(100vh - ${headerHeight}px)`,
              transform: isMenuOpen ? 'translateX(0)' : 'translateX(100%)'
            }}
          >
            <nav className="p-4">
              <div className="space-y-4">
                <div className="border-b border-green-200 pb-4">
                  <Link 
                    to="/business-and-nonprofits"
                    onClick={toggleMenu}
                    className="block py-2 text-lg font-bold text-green-900 hover:bg-green-50 active:bg-green-100 transition-colors"
                  >
                    For Businesses & Nonprofits
                  </Link>
                </div>

                <div className="border-b border-green-200 pb-4">
                  <Link 
                    to="/about-us"
                    onClick={toggleMenu}
                    className="block py-2 text-lg font-bold text-green-900 hover:bg-green-50 active:bg-green-100 transition-colors"
                  >
                    About Us
                  </Link>
                </div>

                <div className="border-b border-green-200 pb-4">
                  <button 
                    onClick={() => toggleSection('resources')}
                    className="w-full flex items-center justify-between py-2 text-lg font-bold text-green-900 hover:bg-green-50 transition-colors"
                  >
                    <span>Resources</span>
                    <span className="text-2xl transform transition-transform duration-200 ease-in-out">
                      {expandedSections.resources ? '−' : '+'}
                    </span>
                  </button>
                  {expandedSections.resources && (
                    <div className="pl-4 pt-2 space-y-2">
                      <Link to="/faqs" onClick={toggleMenu} className="block py-2 text-base hover:bg-green-50 active:bg-green-100 transition-colors text-green-700">
                        FAQs
                      </Link>
                      <Link to="/case-studies" onClick={toggleMenu} className="block py-2 text-base hover:bg-green-50 active:bg-green-100 transition-colors text-green-700">
                        Case Studies
                      </Link>
                      <Link to={PROFESSIONAL_DEVELOPMENT_PATH} onClick={toggleMenu} className="block py-2 text-base hover:bg-green-50 active:bg-green-100 transition-colors text-green-700">
                        Professional Development
                      </Link>
                      <Link to="/services" onClick={toggleMenu} className="block py-2 text-base hover:bg-green-50 active:bg-green-100 transition-colors text-green-700">
                        Services
                      </Link>
                      <Link to="/blog" onClick={toggleMenu} className="block py-2 text-base hover:bg-green-50 active:bg-green-100 transition-colors text-green-700">
                        HalalCred Blog
                      </Link>
                      <Link to="/press-coverage" onClick={toggleMenu} className="block py-2 text-base hover:bg-green-50 active:bg-green-100 transition-colors text-green-700">
                        Press Coverage
                      </Link>
                    </div>
                  )}
                </div>

                <div className="pt-4 space-y-3">
                  <Button
                    onClick={handleForCDFIs}
                    className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 font-semibold py-3 rounded-full transition-all duration-200 transform hover:scale-[1.02] active:scale-100 shadow-md hover:shadow-lg text-base border border-green-500/20"
                  >
                    Become a CDFI Partner
                  </Button>
                  <Button
                    onClick={handleGetStarted}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 font-semibold py-3 rounded-full transition-all duration-200 transform hover:scale-[1.02] active:scale-100 shadow-md hover:shadow-lg text-base border border-green-400/30"
                  >
                    Find Halal Funding
                  </Button>
                </div>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
